import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { appCopy } from '@zibanu/shared';
import { Button, EmptyState } from '@zibanu/ui';
import { forgetAnotherPerson } from '../app/device';
import { useDeps } from '../app/deps';
import { Splash, UpgradeNotice } from '../app/Gate';
import { loginFlow } from '../app/loginFlow';

/**
 * The link "open app" sends in chat (2A): the token rides in the fragment,
 * which no server sees. It is read once, redeemed once with a POST, and
 * cleared from the address bar (spec 3A §6.1).
 */
export function AuthLink() {
  const deps = useDeps();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const started = useRef(false);
  const [bad, setBad] = useState(false);
  // A good link, but the app isn't part of their plan: no session (phase 1 D1).
  const [noApp, setNoApp] = useState(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = hash.replace(/^#/, '');
    void navigate({ to: '/auth/link', hash: '', replace: true });
    if (!/^[a-f0-9]{64}$/.test(token)) {
      setBad(true);
      return;
    }
    void deps.client.auth
      .redeemLink(token, deps.platform.deviceLabel())
      .then(async (res) => {
        if (!res.ok) {
          if (res.error === 'forbidden' && res.reason === 'no_app') setNoApp(true);
          else setBad(true);
          return;
        }
        await forgetAnotherPerson(deps, res.data.person.personId);
        queryClient.setQueryData(['me'], res.data);
        await navigate({ href: loginFlow.takePlace(), replace: true });
      })
      .catch(() => setBad(true));
  }, [deps, hash, navigate, queryClient]);

  if (noApp) {
    return (
      <main className="zw-stop">
        <UpgradeNotice />
      </main>
    );
  }
  if (!bad) return <Splash />;
  return (
    <main className="zw-stop">
      <EmptyState title={appCopy.linkBad()} />
      <Button variant="secondary" onClick={() => void navigate({ to: '/login' })}>
        {appCopy.loginSendCode()}
      </Button>
    </main>
  );
}
